type CapitalizeWord<S extends string> = S extends `${infer First}${infer Rest}`
  ? `${Uppercase<First>}${Rest}`
  : S

/**
 * @example
 * ```ts
 * KebabToPascal<'my-component-name'> // 'MyComponentName'
 * ```
 */
export type KebabToPascal<S extends string> =
  S extends `${infer Head}-${infer Tail}`
    ? `${CapitalizeWord<Head>}${KebabToPascal<CapitalizeWord<Tail>>}`
    : CapitalizeWord<S>

/**
 * @example
 * ```ts
 * PascalToKebab<'MyComponentName'> // 'my-component-name'
 * ```
 */
export type PascalToKebab<S extends string> =
  S extends `${infer First}${infer Rest}`
    ? `${LowercaseChar<First>}${KebabHelper<Rest>}`
    : S

type KebabHelper<S extends string> = S extends `${infer First}${infer Rest}`
  ? First extends AlphabeticChar
    ? First extends Lowercase<First>
      ? `${First}${KebabHelper<Rest>}`
      : `-${Lowercase<First>}${KebabHelper<Rest>}`
    : `-${First}${KebabHelper<Rest>}`
  : S

type AlphabeticChar =
  | 'a'
  | 'b'
  | 'c'
  | 'd'
  | 'e'
  | 'f'
  | 'g'
  | 'h'
  | 'i'
  | 'j'
  | 'k'
  | 'l'
  | 'm'
  | 'n'
  | 'o'
  | 'p'
  | 'q'
  | 'r'
  | 's'
  | 't'
  | 'u'
  | 'v'
  | 'w'
  | 'x'
  | 'y'
  | 'z'
  | 'A'
  | 'B'
  | 'C'
  | 'D'
  | 'E'
  | 'F'
  | 'G'
  | 'H'
  | 'I'
  | 'J'
  | 'K'
  | 'L'
  | 'M'
  | 'N'
  | 'O'
  | 'P'
  | 'Q'
  | 'R'
  | 'S'
  | 'T'
  | 'U'
  | 'V'
  | 'W'
  | 'X'
  | 'Y'
  | 'Z'

type LowercaseChar<C extends string> = C extends AlphabeticChar
  ? Lowercase<C>
  : C

export const kebabToPascal = <S extends string>(str: S): KebabToPascal<S> => {
  const camelCase = str.replace(/-([a-zA-Z0-9])/g, (_, char) =>
    typeof char === 'string' ? char.toUpperCase() : '',
  )
  return (camelCase.charAt(0).toUpperCase() +
    camelCase.slice(1)) as KebabToPascal<S>
}
